import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft, Sun, Moon, User, LogOut, Loader2, FileText, Clock,
  CheckCircle, XCircle, Trophy, ChevronRight, AlertTriangle, Brain,
  Sparkles, Send, BookOpen, Shield, BarChart3, Zap, Target, X, Timer
} from 'lucide-react';
import { boardExamsApi, boardExamSimulatorApi } from '../lib/api';
import { FontSizeSlider, useExamFontScale } from '../components/exam/FontSizeSlider';
import { toast } from 'sonner';

// Isolated textarea — completely shielded from parent re-renders (timer, state changes)
const ExamTextarea = React.memo(function ExamTextarea({ questionKey, initialValue, onSave, placeholder, isLong }) {
  const ref = useRef(null);
  return (
    <textarea
      ref={ref}
      defaultValue={initialValue}
      onBlur={e => onSave(e.target.value)}
      placeholder={placeholder}
      className={`w-full p-3 rounded-lg border bg-background resize-y text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none ${isLong ? 'h-40' : 'h-24'}`}
    />
  );
}, (prev, next) => prev.questionKey === next.questionKey);

const SEVERITY_COLOR = {
  Critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
  High: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800',
  Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  Low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
};

export default function BoardExamSimulator() {
  const [fontScale, setFontScale] = useExamFontScale();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [view, setView] = useState('gap');
  const [gapData, setGapData] = useState(null);
  const [gapLoading, setGapLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedBoard, setSelectedBoard] = useState(user?.board || 'CBSE');
  const [selectedGrade, setSelectedGrade] = useState(String(user?.grade || 10));
  const [selectedStream, setSelectedStream] = useState('');
  const [availableStreams, setAvailableStreams] = useState([]);
  const [generating, setGenerating] = useState(false);

  // Original exam state
  const [currentExam, setCurrentExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const pendingTextRef = useRef({});

  // Quick Answer Mode state
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [quickIndex, setQuickIndex] = useState(0);
  const [quickAnswer, setQuickAnswer] = useState(null);
  const [quickChecked, setQuickChecked] = useState(false);
  const [quickScore, setQuickScore] = useState({ correct: 0, wrong: 0, total: 0 });

  // Real Exam Mode state
  const [realExam, setRealExam] = useState(null);
  const [realAnswers, setRealAnswers] = useState({});
  const [realSubmitted, setRealSubmitted] = useState(false);
  const [realTimeLeft, setRealTimeLeft] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const timerRef = useRef(null);

  const BOARD_GRADES = { CBSE: [5,6,7,8,9,10,11,12], ICSE: [5,6,7,8,9,10,11,12], SSC: [5,6,7,8,9,10], HSC: [11,12], IB: [5,6,7,8,9,10,11,12], Cambridge: [5,6,7,8,9,10,11,12] };
  const validGrades = BOARD_GRADES[selectedBoard] || [5,6,7,8,9,10,11,12];

  const handleBoardChange = (board) => {
    setSelectedBoard(board);
    const grades = BOARD_GRADES[board] || [5,6,7,8,9,10,11,12];
    if (!grades.includes(parseInt(selectedGrade))) setSelectedGrade(String(grades[0]));
    setSelectedStream('');
    setAvailableStreams([]);
  };

  useEffect(() => { loadGapAnalysis(); loadSubjects(); }, []);
  useEffect(() => { loadSubjects(); }, [selectedBoard, selectedGrade, selectedStream]);

  const loadGapAnalysis = async () => {
    try { const data = await boardExamSimulatorApi.getGapAnalysis(); setGapData(data); } catch {}
    setGapLoading(false);
  };

  const loadSubjects = async () => {
    try {
      const data = await boardExamsApi.getSubjects(selectedBoard, parseInt(selectedGrade), selectedStream || null);
      setSubjects(data.subjects || []);
      setAvailableStreams(data.streams || []);
    } catch {
      setSubjects(['Mathematics', 'Science', 'English']);
      setAvailableStreams([]);
    }
  };

  // Timer for Real Exam Mode
  useEffect(() => {
    if (view === 'realExam' && realTimeLeft > 0 && !realSubmitted) {
      timerRef.current = setInterval(() => {
        setRealTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); submitRealExam(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [view, realSubmitted]);

  // Original exam timer — runs once, no cascading re-renders
  useEffect(() => {
    if (view !== 'exam') return;
    const t = setInterval(() => {
      setTimeLeft(p => {
        if (!p || p <= 0) { clearInterval(t); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [view]);

  const formatTime = (s) => {
    if (!s || s < 0) return '0:00';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}` : `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // ========== QUICK ANSWER MODE ==========
  const startQuickMode = async () => {
    if (!selectedSubject) { toast.error('Select a subject first'); return; }
    setGenerating(true);
    try {
      const data = await boardExamSimulatorApi.generateQuick(selectedBoard, parseInt(selectedGrade), selectedSubject, 10);
      setQuickQuestions(data.questions || []);
      setQuickIndex(0);
      setQuickAnswer(null);
      setQuickChecked(false);
      setQuickScore({ correct: 0, wrong: 0, total: 0 });
      setView('quickAnswer');
    } catch { toast.error('Failed to generate questions'); }
    setGenerating(false);
  };

  const checkQuickAnswer = () => {
    if (quickAnswer === null) return;
    setQuickChecked(true);
    const q = quickQuestions[quickIndex];
    setQuickScore(prev => ({
      correct: prev.correct + (quickAnswer === q.correct ? 1 : 0),
      wrong: prev.wrong + (quickAnswer !== q.correct ? 1 : 0),
      total: prev.total + 1,
    }));
  };

  const nextQuickQuestion = () => {
    if (quickIndex < quickQuestions.length - 1) {
      setQuickIndex(prev => prev + 1);
      setQuickAnswer(null);
      setQuickChecked(false);
    }
  };

  // ========== REAL EXAM MODE ==========
  const startRealMode = async () => {
    if (!selectedSubject) { toast.error('Select a subject first'); return; }
    setGenerating(true);
    try {
      const data = await boardExamSimulatorApi.generateRealExam(selectedBoard, parseInt(selectedGrade), selectedSubject);
      setRealExam(data);
      setRealAnswers({});
      setRealSubmitted(false);
      setActiveSection(0);
      setRealTimeLeft(data.time_minutes * 60);
      setView('realExam');
    } catch { toast.error('Failed to generate exam paper'); }
    setGenerating(false);
  };

  const submitRealExam = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRealSubmitted(true);
    toast.success('Exam submitted!');
  }, []);

  const getRealExamResults = () => {
    if (!realExam) return { correct: 0, wrong: 0, unanswered: 0, score: 0, maxScore: 0 };
    let correct = 0, wrong = 0, unanswered = 0;
    realExam.sections.forEach((section, si) => {
      section.questions.forEach((q, qi) => {
        const key = `${si}-${qi}`;
        if (realAnswers[key] === undefined) unanswered++;
        else if (realAnswers[key] === q.correct) correct++;
        else wrong++;
      });
    });
    const score = correct * (realExam.marks_correct || 1);
    const maxScore = realExam.total_questions * (realExam.marks_correct || 1);
    return { correct, wrong, unanswered, score, maxScore };
  };

  // ========== ORIGINAL EXAM HANDLERS ==========
  const handleAnswer = (sIdx, qIdx, value) => {
    setAnswers(p => ({ ...p, [`${sIdx}_${qIdx}`]: value }));
  };

  const handleCaseAnswer = (sIdx, qIdx, subIdx, value) => {
    setAnswers(p => {
      const key = `${sIdx}_${qIdx}_case`;
      return { ...p, [key]: { ...(p[key] || {}), [String(subIdx)]: value } };
    });
  };

  const generateSimulation = async () => {
    if (!selectedSubject) { toast.error('Please select a subject'); return; }
    setGenerating(true);
    try {
      const exam = await boardExamSimulatorApi.generate(selectedBoard, parseInt(selectedGrade), selectedSubject);
      setCurrentExam(exam);
      setAnswers({});
      setCurrentSection(0);
      setResult(null);
      setTimeLeft(exam.duration_minutes * 60);
      setView('exam');
      toast.success('Board exam simulation generated!');
    } catch { toast.error('Failed to generate simulation'); }
    setGenerating(false);
  };

  const handleSubmit = async () => {
    if (!currentExam) return;
    setSubmitting(true);
    try {
      // Flush any pending textarea values + read any focused textareas from DOM
      document.querySelectorAll('textarea').forEach(ta => {
        if (ta.dataset && ta.closest('[data-testid]')) {
          const match = ta.closest('[data-testid]')?.dataset?.testid?.match(/sim-q-(\d+)-(\d+)/);
          if (match) pendingTextRef.current[`${match[1]}_${match[2]}`] = ta.value;
        }
      });
      const merged = { ...answers, ...pendingTextRef.current };
      pendingTextRef.current = {};
      const answersList = [];
      currentExam.sections?.forEach((section, sIdx) => {
        section.questions?.forEach((q, qIdx) => {
          const key = `${sIdx}_${qIdx}`;
          const caseKey = `${sIdx}_${qIdx}_case`;
          if (section.question_type === 'case_based') {
            answersList.push({ section_index: sIdx, question_index: qIdx, answer: merged[caseKey] || {} });
          } else {
            answersList.push({ section_index: sIdx, question_index: qIdx, answer: merged[key] });
          }
        });
      });
      const res = await boardExamSimulatorApi.submit({ exam_id: currentExam.exam_id, answers: answersList });
      setResult(res);
      setView('result');
      toast.success('Exam submitted and graded!');
    } catch { toast.error('Submission failed'); }
    setSubmitting(false);
  };

  const getAnsweredCount = () => {
    if (!currentExam) return { answered: 0, total: 0 };
    const merged = { ...answers, ...pendingTextRef.current };
    let answered = 0, total = 0;
    currentExam.sections?.forEach((s, si) => {
      s.questions?.forEach((q, qi) => {
        total++;
        const key = s.question_type === 'case_based' ? `${si}_${qi}_case` : `${si}_${qi}`;
        if (merged[key] !== undefined && merged[key] !== '' && merged[key] !== null) answered++;
      });
    });
    return { answered, total };
  };

  const { answered, total } = getAnsweredCount();

  // ======================== GAP ANALYSIS VIEW ========================
  const GapView = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" data-testid="gap-title">GAP Analysis Report</h2>
        <p className="text-muted-foreground">Comparison between CognitiaIndia exams and actual board patterns</p>
      </div>
      {gapLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : gapData ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 rounded-xl border bg-red-50 dark:bg-red-950/20 text-center">
              <p className="text-2xl font-bold text-red-600">{gapData.gap_count_by_severity?.Critical || 0}</p>
              <p className="text-xs text-red-600 font-medium">Critical Gaps</p>
            </div>
            <div className="p-4 rounded-xl border bg-orange-50 dark:bg-orange-950/20 text-center">
              <p className="text-2xl font-bold text-orange-600">{gapData.gap_count_by_severity?.High || 0}</p>
              <p className="text-xs text-orange-600 font-medium">High Gaps</p>
            </div>
            <div className="p-4 rounded-xl border bg-amber-50 dark:bg-amber-950/20 text-center">
              <p className="text-2xl font-bold text-amber-600">{gapData.gap_count_by_severity?.Medium || 0}</p>
              <p className="text-xs text-amber-600 font-medium">Medium Gaps</p>
            </div>
            <div className="p-4 rounded-xl border bg-blue-50 dark:bg-blue-950/20 text-center">
              <p className="text-2xl font-bold text-blue-600">{gapData.gap_count_by_severity?.Low || 0}</p>
              <p className="text-xs text-blue-600 font-medium">Low Gaps</p>
            </div>
          </div>
          <div className="p-4 rounded-xl border bg-muted/50 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Overall Alignment Score</p>
              <p className="text-xs text-muted-foreground">{gapData.note}</p>
            </div>
            <span className="text-2xl font-bold text-amber-600">{gapData.overall_alignment_score}</span>
          </div>
          <div className="space-y-3" data-testid="gap-list">
            {gapData.gaps?.map((gap, i) => (
              <details key={i} className={`p-4 rounded-xl border ${SEVERITY_COLOR[gap.severity] || ''}`} data-testid={`gap-${gap.id}`}>
                <summary className="cursor-pointer flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                      gap.severity === 'Critical' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                      gap.severity === 'High' ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                      gap.severity === 'Medium' ? 'bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200' :
                      'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                    }`}>{gap.severity}</span>
                    <span className="font-semibold text-sm">{gap.id}: {gap.title}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{gap.category}</span>
                </summary>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="text-xs font-medium text-red-600 mb-1">Current (CognitiaIndia)</p>
                      <p className="text-xs">{gap.current}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="text-xs font-medium text-emerald-600 mb-1">Actual Board Pattern</p>
                      <p className="text-xs">{gap.actual}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium">Boards:</span>
                    {gap.boards_affected?.map(b => (
                      <span key={b} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{b}</span>
                    ))}
                  </div>
                  <div className="p-2 rounded bg-emerald-50 dark:bg-emerald-950/20">
                    <p className="text-xs"><strong>Fix:</strong> {gap.recommendation}</p>
                  </div>
                </div>
              </details>
            ))}
          </div>
          <Button onClick={() => setView('setup')} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="start-simulator-btn">
            <Shield className="w-5 h-5 mr-2" />Launch Board Exam Simulator
          </Button>
        </>
      ) : (
        <p className="text-center text-muted-foreground">Failed to load GAP analysis.</p>
      )}
    </div>
  );

  // ======================== SETUP VIEW ========================
  const SetupView = () => (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => setView('gap')} data-testid="back-to-gap"><ArrowLeft className="w-4 h-4 mr-2" />Back to GAP Analysis</Button>

      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Board Exam Simulator</h2>
        <p className="text-muted-foreground">Choose your board, grade, subject and exam mode</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Board</label>
          <Select value={selectedBoard} onValueChange={handleBoardChange}>
            <SelectTrigger data-testid="sim-board"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['CBSE', 'ICSE', 'SSC', 'HSC', 'IB', 'Cambridge'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Grade</label>
          <Select value={selectedGrade} onValueChange={(v) => { setSelectedGrade(v); setSelectedStream(''); }}>
            <SelectTrigger data-testid="sim-grade"><SelectValue /></SelectTrigger>
            <SelectContent>
              {validGrades.map(g => <SelectItem key={g} value={String(g)}>Class {g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {availableStreams.length > 0 && (
        <div>
          <label className="text-sm font-medium mb-2 block">Stream</label>
          <Select value={selectedStream || 'all'} onValueChange={(v) => setSelectedStream(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="sim-stream"><SelectValue placeholder="All Streams" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Streams</SelectItem>
              {availableStreams.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <label className="text-sm font-medium mb-3 block">Subject</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sim-subject-grid">
          {subjects.map(s => (
            <button key={s} onClick={() => setSelectedSubject(s)}
              className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${selectedSubject === s ? 'border-primary bg-primary/5 shadow-md' : 'border-border hover:border-primary/40'}`}
              data-testid={`sim-subject-${s.toLowerCase().replace(/\s+/g, '-')}`}
            >{s}</button>
          ))}
        </div>
      </div>

      {/* Mode Selection */}
      <div className="p-6 rounded-2xl border bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20 space-y-4" data-testid="mode-selection">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <Target className="w-5 h-5 text-violet-500" />Choose Your Exam Mode
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={startQuickMode}
            disabled={generating || !selectedSubject}
            data-testid="quick-answer-mode-btn"
            className="p-5 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 bg-white dark:bg-card text-left transition-all hover:shadow-lg hover:border-emerald-400 hover:scale-[1.02] group disabled:opacity-50"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Zap className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h4 className="font-bold text-sm group-hover:text-emerald-600 transition-colors">Quick Answer Mode</h4>
                <p className="text-[10px] text-muted-foreground">Instant feedback</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Answer one MCQ at a time. Check your answer immediately and see detailed explanations before moving to the next.</p>
          </button>

          <button
            onClick={startRealMode}
            disabled={generating || !selectedSubject}
            data-testid="real-exam-mode-btn"
            className="p-5 rounded-xl border-2 border-orange-200 dark:border-orange-800 bg-white dark:bg-card text-left transition-all hover:shadow-lg hover:border-orange-400 hover:scale-[1.02] group disabled:opacity-50"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h4 className="font-bold text-sm group-hover:text-orange-600 transition-colors">Real Exam Mode</h4>
                <p className="text-[10px] text-muted-foreground">Full exam simulation</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Timed board exam with all sections, marks, and results at the end. Mimics the actual board exam experience.</p>
          </button>
        </div>
        {generating && (
          <div className="flex items-center justify-center gap-2 py-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Generating exam paper...</span>
          </div>
        )}
      </div>

      <div className="p-4 rounded-xl border bg-muted/30">
        <h4 className="font-medium text-sm mb-2">Exam Format Preview: {selectedBoard} Class {selectedGrade}</h4>
        <p className="text-xs text-muted-foreground">This simulation mimics the actual board exam pattern with proper sections, marks per question, time limit, and pass criteria.</p>
      </div>
    </div>
  );

  // ======================== QUICK ANSWER VIEW ========================
  const QuickAnswerView = () => {
    if (!quickQuestions.length) return null;
    const q = quickQuestions[quickIndex];
    const isLast = quickIndex === quickQuestions.length - 1;
    const isFinished = quickChecked && isLast;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setView('setup')} data-testid="back-from-quick">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Setup
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-xs px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />{quickScore.correct}
            </span>
            <span className="text-xs px-3 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium flex items-center gap-1">
              <XCircle className="w-3 h-3" />{quickScore.wrong}
            </span>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-500" />Quick Answer - {selectedBoard} {selectedSubject}
            </span>
            <span className="text-xs text-muted-foreground">Q {quickIndex + 1} of {quickQuestions.length}</span>
          </div>
          <Progress value={((quickIndex + (quickChecked ? 1 : 0)) / quickQuestions.length) * 100} className="h-2" />
        </div>

        <div className="p-6 rounded-2xl border bg-card" data-testid={`quick-question-${quickIndex}`}>
          <div className="flex items-start gap-3 mb-5">
            <span className="shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">{quickIndex + 1}</span>
            <p className="font-medium text-base leading-relaxed flex-1">{q.question}</p>
          </div>

          <div className="space-y-3 ml-12">
            {q.options?.map((opt, oi) => {
              const isSelected = quickAnswer === oi;
              const isCorrect = oi === q.correct;
              let style = 'border-border hover:border-primary/40 cursor-pointer';
              if (quickChecked) {
                if (isCorrect) style = 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-400';
                else if (isSelected) style = 'border-red-400 bg-red-50 dark:bg-red-950/30';
                else style = 'border-border opacity-60';
              } else if (isSelected) {
                style = 'border-primary bg-primary/5 ring-1 ring-primary';
              }
              return (
                <div key={oi}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all ${style}`}
                  onClick={() => !quickChecked && setQuickAnswer(oi)}
                  data-testid={`quick-option-${oi}`}>
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 ${
                    quickChecked && isCorrect ? 'bg-emerald-500 text-white border-emerald-500' :
                    quickChecked && isSelected ? 'bg-red-500 text-white border-red-500' :
                    isSelected ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground/30'
                  }`}>{String.fromCharCode(65 + oi)}</span>
                  <span className="flex-1 text-sm">{opt}</span>
                  {quickChecked && isCorrect && <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />}
                  {quickChecked && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
                </div>
              );
            })}
          </div>

          {quickChecked && q.explanation && (
            <div className="mt-5 ml-12 p-4 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800" data-testid="quick-explanation">
              <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1 flex items-center gap-1">
                <Brain className="w-3 h-3" />Explanation
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-300">{q.explanation}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          {!quickChecked ? (
            <Button onClick={checkQuickAnswer} disabled={quickAnswer === null}
              className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-emerald-500 to-green-600"
              data-testid="check-answer-btn">
              <CheckCircle className="w-5 h-5 mr-2" />Check Answer
            </Button>
          ) : isFinished ? (
            <div className="flex-1 space-y-3">
              <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border">
                <p className="font-bold text-center text-lg" data-testid="quick-final-score">
                  Final Score: {quickScore.correct}/{quickQuestions.length} ({Math.round(quickScore.correct / quickQuestions.length * 100)}%)
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={startQuickMode} className="flex-1" data-testid="retry-quick-btn">
                  <Sparkles className="w-4 h-4 mr-2" />New Questions
                </Button>
                <Button variant="outline" onClick={() => setView('setup')}>Back to Setup</Button>
              </div>
            </div>
          ) : (
            <Button onClick={nextQuickQuestion}
              className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-blue-500 to-indigo-600"
              data-testid="next-question-btn">
              Next Question <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ======================== REAL EXAM VIEW ========================
  const RealExamView = () => {
    if (!realExam) return null;
    const section = realExam.sections[activeSection];
    const results = realSubmitted ? getRealExamResults() : null;

    return (
      <div className="space-y-4">
        <div className="sticky top-[57px] z-40 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b" data-testid="real-exam-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!realSubmitted && (
                <Button variant="ghost" size="sm" onClick={() => {
                  if (window.confirm('Leave exam? Your progress will be lost.')) {
                    if (timerRef.current) clearInterval(timerRef.current);
                    setView('setup');
                  }
                }} data-testid="exit-real-exam"><X className="w-4 h-4" /></Button>
              )}
              <div>
                <h3 className="text-sm font-bold">{realExam.exam_name} - Real Exam</h3>
                <p className="text-[10px] text-muted-foreground">{realExam.exam_pattern}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!realSubmitted && (
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-sm font-bold ${
                  realTimeLeft < 300 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 animate-pulse' : 'bg-muted'
                }`} data-testid="exam-timer">
                  <Clock className="w-4 h-4" />{formatTime(realTimeLeft)}
                </div>
              )}
              <span className="text-xs text-muted-foreground">
                {Object.keys(realAnswers).length}/{realExam.total_questions} answered
              </span>
            </div>
          </div>
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1" data-testid="section-tabs">
            {realExam.sections.map((s, si) => {
              const answeredInSection = s.questions.filter((_, qi) => realAnswers[`${si}-${qi}`] !== undefined).length;
              return (
                <button key={si} onClick={() => setActiveSection(si)}
                  data-testid={`real-section-tab-${si}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    activeSection === si ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                  }`}>
                  {s.name} <span className="ml-1.5 opacity-70">({answeredInSection}/{s.questions.length})</span>
                </button>
              );
            })}
          </div>
        </div>

        {realSubmitted && results && (
          <div className="p-6 rounded-2xl border bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20" data-testid="real-exam-results">
            <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-violet-500" />Exam Results
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <div className="p-3 rounded-xl bg-white dark:bg-card border">
                <p className="text-xs text-muted-foreground">Score</p>
                <p className="text-2xl font-bold text-primary" data-testid="real-exam-score">{results.score}/{results.maxScore}</p>
              </div>
              <div className="p-3 rounded-xl bg-white dark:bg-card border">
                <p className="text-xs text-muted-foreground">Percentage</p>
                <p className="text-2xl font-bold text-primary">{results.maxScore > 0 ? Math.round(results.score / results.maxScore * 100) : 0}%</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                <p className="text-xs text-emerald-600">Correct</p>
                <p className="text-2xl font-bold text-emerald-600">{results.correct}</p>
              </div>
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                <p className="text-xs text-red-600">Wrong</p>
                <p className="text-2xl font-bold text-red-600">{results.wrong}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-600">Unanswered</p>
                <p className="text-2xl font-bold text-amber-600">{results.unanswered}</p>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={startRealMode} data-testid="retake-exam-btn">
                <Sparkles className="w-4 h-4 mr-2" />Retake Exam
              </Button>
              <Button variant="outline" onClick={() => setView('setup')}>Back to Setup</Button>
            </div>
          </div>
        )}

        <div className="space-y-4" data-testid="real-exam-questions">
          {section?.questions.map((q, qi) => {
            const key = `${activeSection}-${qi}`;
            const answered = realAnswers[key];
            return (
              <div key={qi} className={`p-5 rounded-xl border ${
                realSubmitted
                  ? answered === q.correct ? 'border-emerald-300 bg-emerald-50/30 dark:border-emerald-800 dark:bg-emerald-950/20'
                    : answered !== undefined ? 'border-red-300 bg-red-50/30 dark:border-red-800 dark:bg-red-950/20'
                      : 'border-amber-300 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/20'
                  : 'border-border'
              }`} data-testid={`real-q-${activeSection}-${qi}`}>
                <div className="flex items-start gap-3 mb-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{qi + 1}</span>
                  <p className="font-medium text-sm flex-1">{q.question}</p>
                  {realSubmitted && answered === q.correct && <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />}
                  {realSubmitted && answered !== undefined && answered !== q.correct && <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
                  {realSubmitted && answered === undefined && <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />}
                </div>
                {q.options?.length > 0 && (
                  <div className="space-y-2 ml-10">
                    {q.options.map((opt, oi) => {
                      let optStyle = 'border-border hover:border-primary/40 cursor-pointer';
                      if (realSubmitted) {
                        if (oi === q.correct) optStyle = 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30';
                        else if (answered === oi) optStyle = 'border-red-400 bg-red-50 dark:bg-red-950/30';
                        else optStyle = 'border-border opacity-60';
                      } else if (answered === oi) {
                        optStyle = 'border-primary bg-primary/5';
                      }
                      return (
                        <div key={oi}
                          className={`flex items-center space-x-3 p-2.5 rounded-lg border transition-all ${optStyle}`}
                          onClick={() => !realSubmitted && setRealAnswers(prev => ({ ...prev, [key]: oi }))}
                          data-testid={`real-option-${activeSection}-${qi}-${oi}`}>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border shrink-0 ${
                            (realSubmitted && oi === q.correct) ? 'bg-emerald-500 text-white border-emerald-500' :
                            (realSubmitted && answered === oi) ? 'bg-red-500 text-white border-red-500' :
                            answered === oi ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground/30'
                          }`}>{String.fromCharCode(65 + oi)}</span>
                          <span className="flex-1 text-sm">{opt}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {realSubmitted && q.explanation && (
                  <p className="text-xs text-muted-foreground mt-3 ml-10 p-2 rounded bg-muted/50">{q.explanation}</p>
                )}
              </div>
            );
          })}
        </div>

        {!realSubmitted && (
          <Button onClick={() => {
            if (window.confirm(`Submit exam? You have answered ${Object.keys(realAnswers).length} of ${realExam.total_questions} questions.`)) {
              submitRealExam();
            }
          }}
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-red-600"
            data-testid="submit-real-exam-btn">
            <FileText className="w-5 h-5 mr-2" />Submit Exam
          </Button>
        )}
      </div>
    );
  };

  // ======================== ORIGINAL EXAM VIEW (kept for compatibility) ========================
  const ExamView = () => {
    if (!currentExam) return null;
    const sections = currentExam.sections || [];
    const sec = sections[currentSection];
    if (!sec) return null;

    return (
      <div className="space-y-4" style={{ zoom: fontScale }} data-testid="board-simulator-exam">
        <div className="flex justify-end">
          <FontSizeSlider scale={fontScale} onChange={setFontScale} />
        </div>
        <div className="p-3 rounded-xl bg-muted/50 border">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="font-semibold text-sm">{currentExam.title}</span>
              <span className="text-xs text-muted-foreground ml-2">| Total: {currentExam.total_theory_marks} marks</span>
            </div>
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full font-mono text-sm font-bold ${timeLeft < 300 ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 animate-pulse' : 'bg-primary/10 text-primary'}`}>
              <Clock className="w-4 h-4" />{formatTime(timeLeft || 0)}
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{answered}/{total} questions answered</span>
            <span>Pass: {currentExam.pass_percentage}%</span>
          </div>
          <Progress value={(answered / total) * 100} className="h-1.5 mt-2" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sections.map((s, si) => (
            <button key={si} onClick={() => setCurrentSection(si)}
              className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                si === currentSection ? 'bg-primary text-primary-foreground shadow' : 'bg-muted hover:bg-muted/80'
              }`} data-testid={`section-tab-${si}`}>
              {s.section_name} ({s.questions?.length}Q x {s.marks_per_question}m)
            </button>
          ))}
        </div>
        <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
          <h3 className="font-semibold text-sm">{sec.section_name}: {sec.title}</h3>
          <p className="text-xs text-muted-foreground mt-1">{sec.instructions}</p>
        </div>
        <div className="space-y-4">
          {sec.questions?.map((q, qi) => {
            const key = `${currentSection}_${qi}`;
            const caseKey = `${currentSection}_${qi}_case`;
            return (
              <div key={qi} className="p-4 rounded-xl border bg-card" data-testid={`sim-q-${currentSection}-${qi}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground">Q{qi + 1}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{sec.marks_per_question} marks</span>
                </div>
                {sec.question_type === 'case_based' && q.passage && (
                  <div className="p-3 rounded-lg bg-muted/50 mb-3 text-sm border-l-4 border-primary">{q.passage}</div>
                )}
                <p className="font-medium text-sm mb-3">{q.question}</p>
                {(sec.question_type === 'mcq' && q.options) && (
                  <RadioGroup value={String(answers[key] ?? '')} onValueChange={v => handleAnswer(currentSection, qi, parseInt(v))} className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className={`flex items-center space-x-3 p-2.5 rounded-lg border transition-all cursor-pointer ${answers[key] === oi ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                        onClick={() => handleAnswer(currentSection, qi, oi)}>
                        <RadioGroupItem value={String(oi)} id={`sim-${currentSection}-${qi}-${oi}`} />
                        <Label htmlFor={`sim-${currentSection}-${qi}-${oi}`} className="flex-1 cursor-pointer text-sm">{opt}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
                {sec.question_type === 'case_based' && q.sub_questions && (
                  <div className="space-y-3 mt-2">
                    {q.sub_questions.map((sq, si) => (
                      <div key={si} className="pl-4 border-l-2 border-muted">
                        <p className="text-sm mb-2">({String.fromCharCode(105 + si)}) {sq.question}</p>
                        {sq.options && (
                          <RadioGroup value={String((answers[caseKey] || {})[String(si)] ?? '')}
                            onValueChange={v => handleCaseAnswer(currentSection, qi, si, parseInt(v))} className="space-y-1.5">
                            {sq.options.map((opt, oi) => (
                              <div key={oi} className={`flex items-center space-x-2 p-2 rounded-lg border text-xs ${(answers[caseKey] || {})[String(si)] === oi ? 'border-primary bg-primary/5' : 'border-border'}`}
                                onClick={() => handleCaseAnswer(currentSection, qi, si, oi)}>
                                <RadioGroupItem value={String(oi)} id={`case-${currentSection}-${qi}-${si}-${oi}`} className="scale-90" />
                                <Label htmlFor={`case-${currentSection}-${qi}-${si}-${oi}`} className="flex-1 cursor-pointer">{opt}</Label>
                              </div>
                            ))}
                          </RadioGroup>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {(sec.question_type === 'short_answer' || sec.question_type === 'long_answer') && (
                  <div>
                    <ExamTextarea
                      questionKey={key}
                      initialValue={answers[key] || ''}
                      onSave={val => { pendingTextRef.current[key] = val; handleAnswer(currentSection, qi, val); }}
                      placeholder={`Write your answer here... ${q.word_limit ? `(${q.word_limit})` : ''}`}
                      isLong={sec.question_type === 'long_answer'}
                    />
                    {q.word_limit && <p className="text-xs text-muted-foreground mt-1">Suggested: {q.word_limit}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="outline" disabled={currentSection === 0} onClick={() => setCurrentSection(p => p - 1)}>Previous Section</Button>
          {currentSection < sections.length - 1 ? (
            <Button onClick={() => setCurrentSection(p => p + 1)}>Next Section <ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-gradient-to-r from-emerald-500 to-green-600" data-testid="submit-simulation-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Grading...</> : <><Send className="w-4 h-4 mr-2" />Submit Exam</>}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ======================== RESULT VIEW ========================
  const ResultView = () => {
    if (!result) return null;
    return (
      <div className="space-y-6">
        <div className={`text-center p-8 rounded-2xl border-2 ${result.passed ? 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20'}`}>
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${result.passed ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-red-100 dark:bg-red-900/40'}`}>
            {result.passed ? <Trophy className="w-10 h-10 text-emerald-600" /> : <XCircle className="w-10 h-10 text-red-500" />}
          </div>
          <h2 className="text-3xl font-bold mb-1" data-testid="sim-result-percentage">{result.percentage}%</h2>
          <p className="text-muted-foreground mb-1">{result.total_scored} / {result.total_max} marks</p>
          {result.board_grade && <p className="text-lg font-semibold text-primary mb-2">Grade: {result.board_grade}</p>}
          <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${result.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {result.passed ? `PASSED (Min: ${result.pass_percentage}%)` : `BELOW PASSING (Min: ${result.pass_percentage}%)`}
          </span>
        </div>
        <div className="space-y-3">
          <h3 className="font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4" />Section-wise Performance</h3>
          {result.section_results?.map((sr, si) => {
            const pct = sr.max > 0 ? Math.round(sr.scored / sr.max * 100) : 0;
            return (
              <details key={si} className="rounded-xl border" data-testid={`section-result-${si}`}>
                <summary className="p-4 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{sr.section_name}: {sr.title}</span>
                    <span className={`text-sm font-bold ${pct >= 60 ? 'text-emerald-600' : pct >= 33 ? 'text-amber-600' : 'text-red-600'}`}>
                      {sr.scored}/{sr.max} ({pct}%)
                    </span>
                  </div>
                  <Progress value={pct} className="h-2 mt-2" />
                </summary>
                {sr.results?.length > 0 && (
                  <div className="px-4 pb-4 space-y-2 border-t mt-2 pt-3">
                    {sr.results.map((r, ri) => {
                      const section = currentExam?.sections?.[si];
                      const qData = section?.questions?.[r.question_index];
                      return (
                        <div key={ri} className={`p-3 rounded-lg border text-sm ${
                          r.correct === true ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-800 dark:bg-emerald-950/20'
                            : r.correct === false ? 'border-red-200 bg-red-50/30 dark:border-red-800 dark:bg-red-950/20'
                              : 'border-border'
                        }`} data-testid={`sim-result-${si}-${ri}`}>
                          <div className="flex items-start justify-between mb-1">
                            <span className="font-medium text-xs flex-1">
                              Q{r.question_index + 1}: {qData?.question || ''}
                            </span>
                            <span className={`text-xs font-bold ml-2 shrink-0 ${r.marks_awarded === r.max_marks ? 'text-emerald-600' : r.marks_awarded > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                              {r.marks_awarded}/{r.max_marks}
                            </span>
                          </div>
                          {r.correct === false && qData?.options && (
                            <div className="text-xs space-y-0.5 mt-1">
                              <p className="text-red-500">Your answer: {String.fromCharCode(65 + (r.your_answer ?? 0))}. {qData.options[r.your_answer]}</p>
                              <p className="text-emerald-600">Correct: {String.fromCharCode(65 + (r.correct_answer ?? qData.correct))}. {qData.options[r.correct_answer ?? qData.correct]}</p>
                            </div>
                          )}
                          {(r.explanation || qData?.explanation) && (
                            <div className="mt-2 p-2 rounded bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                              <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-0.5 flex items-center gap-1"><BookOpen className="w-3 h-3" />Explanation</p>
                              <p className="text-xs text-blue-800 dark:text-blue-300">{r.explanation || qData?.explanation}</p>
                            </div>
                          )}
                          {r.feedback && (
                            <div className="mt-1.5 p-2 rounded bg-muted/50 text-xs"><span className="font-medium">Feedback:</span> {r.feedback}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </details>
            );
          })}
        </div>
        <div className="flex gap-3">
          <Button onClick={() => { setView('setup'); setCurrentExam(null); setResult(null); }} className="flex-1" data-testid="new-simulation-btn">
            <Sparkles className="w-4 h-4 mr-2" />New Simulation
          </Button>
          <Button variant="outline" onClick={() => setView('gap')}>
            <BarChart3 className="w-4 h-4 mr-2" />GAP Analysis
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/exam-module')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold">Board Exam Simulator</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full"><User className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/profile')}><User className="w-4 h-4 mr-2" />Profile</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {view === 'gap' && <GapView />}
        {view === 'setup' && <SetupView />}
        {view === 'quickAnswer' && <QuickAnswerView />}
        {view === 'realExam' && <RealExamView />}
        {view === 'exam' && <ExamView />}
        {view === 'result' && <ResultView />}
      </main>
    </div>
  );
}
